import Api from 'api/base/axios.config';
import {
  HistoryChangeEntry,
  HistoryDiffEntryList,
  HistoryDiffResponseShape,
  HistoryEntryList,
  HistoryType,
} from 'api/history/types';
import { build_search_params } from 'utils';

const BASE_URL = '/datamigrationhistory';
const PART_TYPE_HISTORY_URL = '/datamigrationparttype/history';
const DEFAULT_DIFF_LIMIT = 20;

const normalizeTenantId = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const tenantId = value.trim();
  return tenantId || undefined;
};

const getDiffArraySource = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const payload = value as HistoryDiffResponseShape;
  const possibleValues = [
    payload.changes,
    payload.change_details,
    payload.details,
    payload.diff,
    payload.diffs,
    payload.items,
    payload.results,
    payload.data,
  ];

  for (const candidate of possibleValues) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  for (const candidate of possibleValues) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue;
    }

    const nestedArray = getDiffArraySource(candidate);
    if (nestedArray.length) {
      return nestedArray;
    }
  }

  return [];
};

const normalizeHistoryDiffEntries = (value: unknown): HistoryDiffEntryList => {
  return getDiffArraySource(value).reduce<HistoryDiffEntryList>((accumulator, entryValue) => {
    if (!entryValue || typeof entryValue !== 'object') {
      return accumulator;
    }

    accumulator.push(entryValue as HistoryChangeEntry);
    return accumulator;
  }, []);
};

const getHistoryArraySource = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const payload = value as Record<string, unknown>;
  const possibleValues = [payload.items, payload.results, payload.data, payload.history, payload.entries];

  for (const candidate of possibleValues) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  for (const candidate of possibleValues) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue;
    }

    const nestedArray = getHistoryArraySource(candidate);
    if (nestedArray.length) {
      return nestedArray;
    }
  }

  return [];
};

const normalizeHistoryEntries = (value: unknown): HistoryEntryList => {
  return getHistoryArraySource(value).reduce<HistoryEntryList>((accumulator, entryValue) => {
    if (!entryValue || typeof entryValue !== 'object') {
      return accumulator;
    }

    accumulator.push(entryValue as HistoryEntryList[number]);
    return accumulator;
  }, []);
};

const fetchHistoryEntries = async (type: HistoryType, id: string, tenantId?: string): Promise<HistoryEntryList> => {
  const params = build_search_params({
    type,
    id,
    tenant_id: tenantId,
  });

  const response = await Api.get(`${BASE_URL}?${params}`);
  return normalizeHistoryEntries(response?.data);
};

const fetchPartTypeHistoryEntries = async (partTypeId: string): Promise<HistoryEntryList> => {
  const params = build_search_params({
    part_type_id: partTypeId,
  });

  const response = await Api.get(`${PART_TYPE_HISTORY_URL}?${params}`);
  return normalizeHistoryEntries(response?.data);
};

const fetchHistoryDiff = async (
  partTypeId: string,
): Promise<HistoryDiffEntryList> => {
  const params = build_search_params({
    part_type_id: partTypeId,
  });

  const response = await Api.get(`${PART_TYPE_HISTORY_URL}?${params}`);
  return normalizeHistoryDiffEntries(response?.data);
};

// get history entries
export const getHistoryEntries = async (
  type: HistoryType,
  id: string,
  tenantId?: string,
): Promise<HistoryEntryList> => {
  const normalizedTenantId = normalizeTenantId(tenantId);
  if (type === 'PART_TYPE') {
    return fetchPartTypeHistoryEntries(id);
  }

  return fetchHistoryEntries(type, id, normalizedTenantId);
};

export const getHistoryDiff = async (
  partTypeId: string,
  tenantId?: string,
  limit = DEFAULT_DIFF_LIMIT,
): Promise<HistoryDiffEntryList> => {
  void tenantId;
  void limit;
  return fetchHistoryDiff(partTypeId);
};
